import React, { useState } from 'react';
import { Event } from '../types';
import { EventCard } from '../components/EventCard';
import { Search, Frown } from 'lucide-react';

interface EventsPageProps {
  upcoming_events: Event[];
  past_events: Event[];
}

const EventsPage: React.FC<EventsPageProps> = ({ upcoming_events, past_events }) => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'All' | 'In-Person' | 'Virtual'>('All');

  const currentList = activeTab === 'upcoming' ? upcoming_events : past_events;

  const filteredEvents = currentList.filter((event) => {
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      event.title.toLowerCase().includes(query) ||
      (event.plain_text_details && event.plain_text_details.toLowerCase().includes(query)) ||
      event.location.toLowerCase().includes(query);

    const matchesType =
      filterType === 'All' ||
      (filterType === 'In-Person' && event.location !== 'Online') ||
      (filterType === 'Virtual' && event.location === 'Online');

    return matchesSearch && matchesType;
  });

  return (
    <div>
      <section className='py-16 text-center bg-muted'>
        <div className='container mx-auto px-6'>
          <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3 opacity-0 animate-fade-in-up'>
            Join Us
          </p>
          <h1 className='text-4xl font-bold text-foreground font-heading opacity-0 animate-fade-in-up'>
            Events <span className='text-primary'>& Workshops</span>
          </h1>
          <p
            className='text-lg text-muted-foreground mt-2 opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            Connect, learn, and grow with our community.
          </p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div
            className='flex flex-col md:flex-row justify-between items-center mb-12 gap-6 opacity-0 animate-fade-in'
            style={{ animationDelay: '0.25s' }}
          >
            <div className='inline-flex rounded-md shadow-sm bg-card border border-border p-1 order-2 md:order-1'>
              <button
                onClick={() => setActiveTab('upcoming')}
                className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                  activeTab === 'upcoming'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setActiveTab('past')}
                className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                  activeTab === 'past'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                Past
              </button>
            </div>

            <div className='flex flex-col sm:flex-row w-full md:w-auto gap-4 order-1 md:order-2'>
              <div className='relative min-w-[150px]'>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  className='w-full appearance-none bg-background border border-input rounded-md py-3 pl-4 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow shadow-sm cursor-pointer'
                  aria-label='Filter events by type'
                >
                  <option value='All'>All Types</option>
                  <option value='In-Person'>In-Person</option>
                  <option value='Virtual'>Virtual</option>
                </select>
              </div>

              <div className='relative w-full md:w-80'>
                <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground'>
                  <Search className='h-5 w-5' />
                </span>
                <input
                  type='text'
                  placeholder='Search title, location...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='w-full bg-background border border-input rounded-md py-3 pl-10 pr-4 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow shadow-sm'
                  aria-label='Search events'
                />
              </div>
            </div>
          </div>

          {filteredEvents.length > 0 ? (
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8' key={`${activeTab}-${searchQuery}-${filterType}`}>
              {filteredEvents.map((event, index) => (
                <div
                  key={event.title}
                  className='opacity-0 animate-fade-in-up'
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <EventCard event={event} isUpcoming={activeTab === 'upcoming'} />
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-16 opacity-0 animate-fade-in bg-card rounded-lg border border-border border-dashed'>
              <div className='text-muted-foreground mb-2'>
                <Frown className='h-12 w-12 mx-auto mb-3 text-muted-foreground/50' />
              </div>
              <h3 className='text-xl font-semibold text-foreground'>No Events Found</h3>
              <p className='text-muted-foreground mt-1'>Try adjusting your search or filter settings.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
